import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultDashboard } from '../../utils/roleAccess';
import { siteService } from '../../services/siteService';
import { clientService } from '../../services/clientService';
import { dailyTaskService } from '../../services/taskService';
import { taskPhotoService } from '../../services/taskPhotoService';
import { snagService } from '../../services/snagService';
import { incidentService } from '../../services/incidentService';
import { nearMissService } from '../../services/nearMissService';
import { documentService } from '../../services/documentService';
import { siteDiaryService } from '../../services/siteDiaryService';

import { supabase } from '../../lib/supabase';
import ReportSelector from './components/ReportSelector';
import ReportView from './components/ReportView';
import AIDailyReportModal from '../../components/AIDailyReportModal';

const DailySiteReport = () => {
  const [showAiModal, setShowAiModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();

  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(searchParams?.get('siteId') || '');
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [loading, setLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [siteVisibility, setSiteVisibility] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);

  const role = userProfile?.role;

  const breadcrumbItems = [
    { label: 'Home', href: getDefaultDashboard(role) },
    { label: 'Daily Site Report' },
  ];

  useEffect(() => {
    loadSites();
  }, [role]);

  const loadSites = async () => {
    try {
      setSitesLoading(true);
      if (role === 'client') {
        const result = await clientService?.getSharedSites();
        if (result?.error) throw result?.error;
        setSites(result?.data || []);
      } else if (role === 'admin') {
        const result = await siteService?.getAllSites();
        setSites(result?.data || []);
      } else if (role === 'site_manager') {
        const result = await siteService?.getSitesByUser();
        setSites(result?.data || []);
      } else {
        setSites([]);
      }
    } catch (err) {
      console.error('[DailySiteReport] load data failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading sites: ${err?.message}`);
      } else {
        setError(`Failed to load sites: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setSitesLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedSiteId || !selectedDate) {
      setError('Please select a site and date.');
      return;
    }
    setError('');
    setLoading(true);
    setReportData(null);

    try {
      // Get current user profile for "generated by"
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('full_name, company_id')
        ?.eq('id', user?.id)
        ?.maybeSingle();

      // Get site info
      const siteResult = await siteService?.getSiteById(selectedSiteId);
      const site = siteResult?.data;

      // For clients, get visibility flags
      let visibility = null;
      if (role === 'client') {
        const sharedSite = sites?.find(s => s?.id === selectedSiteId);
        visibility = sharedSite?.siteVisibility || null;
        setSiteVisibility(visibility);
      }

      // Date range for the selected day
      const dayStart = `${selectedDate}T00:00:00`;
      const dayEnd = `${selectedDate}T23:59:59`;

      // Fetch all data in parallel
      const [
        tasksResult,
        taskPhotosResult,
        snagsBySiteResult,
        incidentsResult,
        nearMissesResult,
        documentsResult,
      ] = await Promise.all([
        // Tasks completed on this date
        dailyTaskService?.getForSite(selectedSiteId, selectedDate),
        // Task photos uploaded on this date
        taskPhotoService?.getPhotosBySite(selectedSiteId, {
          dateFrom: dayStart,
          dateTo: dayEnd,
        }),
        // Snags for the site (we'll filter by date below)
        snagService?.getSnagsBySite(selectedSiteId),
        // Incidents for the site (filter by date)
        incidentService?.getIncidentsBySite(selectedSiteId),
        // Near misses for the site (filter by date)
        nearMissService?.getNearMissesForSite(selectedSiteId),
        // Documents for the site (filter by date)
        documentService?.getDocumentsBySite(selectedSiteId),
      ]);

      // Aggregator: any sub-source error is logged but doesn't fail the
      // whole report — the user still gets the sources that succeeded.
      if (taskPhotosResult?.error) {
        console.error('[DailySiteReport] task photos failed:', { code: taskPhotosResult?.error?.code, message: taskPhotosResult?.error?.message });
      }

      // Filter tasks completed on the selected date
      const completedTasks = (tasksResult?.data || [])?.filter(t => t?.status === 'completed');

      // Filter snags raised on the selected date
      const snagsOnDate = (snagsBySiteResult?.data || [])?.filter(snag => {
        if (!snag?.created_at) return false;
        const snagDate = snag?.created_at?.split('T')?.[0];
        return snagDate === selectedDate;
      });

      // Filter incidents on the selected date
      const incidentsOnDate = (incidentsResult?.data || [])?.filter(inc => {
        const incDate = inc?.incident_date || inc?.created_at?.split('T')?.[0];
        return incDate === selectedDate;
      });

      // Filter near misses on the selected date
      const nearMissesOnDate = (nearMissesResult?.data || [])?.filter(nm => {
        const nmDate = nm?.incident_date;
        return nmDate === selectedDate;
      });

      // Filter documents: uploaded on this date OR expiring on this date
      const allDocs = documentsResult?.data || [];
      const docsOnDate = allDocs?.filter(doc => {
        const uploadedDate = doc?.created_at?.split('T')?.[0];
        const expiryDate = doc?.expiry_date;
        const uploadedToday = uploadedDate === selectedDate;
        const expiringToday = expiryDate === selectedDate;
        if (uploadedToday) return true;
        if (expiringToday) return true;
        return false;
      })?.map(doc => ({
        ...doc,
        _reason: doc?.expiry_date === selectedDate ? 'expiring' : 'uploaded',
      }));

      // Get task completion photos for completed tasks
      let completionPhotos = [];
      if (completedTasks?.length > 0) {
        const { data: compPhotos } = await supabase
          ?.from('task_completion_photos')
          ?.select('*')
          ?.in('task_id', completedTasks?.map(t => t?.id))
          ?.gte('uploaded_at', dayStart)
          ?.lte('uploaded_at', dayEnd);
        completionPhotos = compPhotos || [];
      }

      // Combine all photos (task_photos + task_completion_photos)
      const allPhotos = [...(taskPhotosResult?.data || []), ...completionPhotos];

      // Get site diary entry for this date
      let diary = null;
      if (profile?.company_id) {
        const diaryResult = await siteDiaryService?.getEntriesForSite(selectedSiteId, profile?.company_id);
        const entries = diaryResult?.data || [];
        diary = entries?.find(e => e?.record_date === selectedDate) || null;
      }

      setReportData({
        site,
        date: selectedDate,
        generatedBy: profile?.full_name || user?.email || 'Unknown',
        generatedAt: new Date()?.toISOString(),
        tasks: completedTasks,
        photos: allPhotos,
        snags: snagsOnDate,
        incidents: incidentsOnDate,
        nearMisses: nearMissesOnDate,
        documents: docsOnDate,
        diary,
      });
    } catch (err) {
      console.error('[DailySiteReport] generate report failed:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!reportData) return;
    setPdfExporting(true);
    try {
      const html2pdf = (await import('html2pdf.js'))?.default;
      const element = document.getElementById('daily-site-report-content');
      if (!element) {
        window.print();
        return;
      }
      const siteName = reportData?.site?.site_name?.replace(/\s+/g, '-') || 'Site';
      const date = reportData?.date || new Date()?.toISOString()?.split('T')?.[0];
      const filename = `Daily-Site-Report-${siteName}-${date}.pdf`;
      const opt = {
        margin: 10,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };
      await html2pdf()?.set(opt)?.from(element)?.save();
    } catch (err) {
      console.error('PDF export failed:', err);
      window.print();
    } finally {
      setPdfExporting(false);
    }
  };

  const handleBack = () => {
    setReportData(null);
    setError('');
  };

  // Access control: only admin, site_manager, client
  if (role && !['admin', 'site_manager', 'client']?.includes(role)) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center py-16">
            <Icon name="Lock" size={48} className="text-muted-foreground mb-4" />
            <p className="text-muted-foreground">You do not have access to this page.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Daily Site Report — SiteProof</title>
        <style>{`
          @media print {
            header, nav, .print\\:hidden { display: none !important; }
            body { background: white !important; }
            .bg-background { background: white !important; }
            .bg-card { background: white !important; box-shadow: none !important; }
            .shadow-elevation-2 { box-shadow: none !important; }
            main { padding-top: 0 !important; }
            .print\\:shadow-none { box-shadow: none !important; }
            .print\\:border-0 { border: none !important; }
            .print\\:border { border: 1px solid #d1d5db !important; }
            .print\\:border-gray-300 { border-color: #d1d5db !important; }
            .print\\:mb-4 { margin-bottom: 1rem !important; }
            .print\\:p-0 { padding: 0 !important; }
            .print\\:grid-cols-6 { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; }
          }
        `}</style>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          {/* Breadcrumb — screen only */}
          <div className="mb-6 print:hidden">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Page title — screen only */}
          {!reportData && (
            <div className="mb-2 print:hidden flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Daily Site Report</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate a rules-based daily report for any site and date.
                </p>
              </div>
              <button
                onClick={() => setShowAiModal(true)}
                disabled={!selectedSiteId || !selectedDate}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-accent/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors flex-shrink-0"
                title={!selectedSiteId || !selectedDate ? 'Select a site and date first' : 'Generate an AI-written daily report'}
              >
                <Icon name="Sparkles" size={16} />
                Generate AI Report
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 print:hidden">
              <Icon name="AlertCircle" size={16} className="text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Selector state */}
          {!reportData && (
            <ReportSelector
              sites={sites}
              selectedSiteId={selectedSiteId}
              selectedDate={selectedDate}
              onSiteChange={setSelectedSiteId}
              onDateChange={setSelectedDate}
              onGenerate={handleGenerate}
              loading={loading || sitesLoading}
            />
          )}

          {/* Report view */}
          {reportData && (
            <ReportView
              reportData={reportData}
              onBack={handleBack}
              onExportPDF={handleExportPDF}
              siteVisibility={siteVisibility}
              userRole={role}
              pdfExporting={pdfExporting}
            />
          )}
        </main>
      </div>

      <AIDailyReportModal
        isOpen={showAiModal}
        onClose={() => setShowAiModal(false)}
        siteId={selectedSiteId}
        siteName={sites?.find(s => s?.id === selectedSiteId)?.site_name || ''}
        companyId={userProfile?.company_id}
      />
    </>
  );
};

export default DailySiteReport;
